/**
 * Shared utility functions for task date handling
 * Used across TaskItem, TasksPanel, TasksPage components
 */

import type { Task } from '@/server/modules/tasks/db/schema'

/**
 * Format a date for display in task lists
 */
export function formatTaskDate(timestamp: Date | null): string | null {
  if (!timestamp) return null
  return new Date(timestamp).toLocaleDateString('en-AU', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Check if a task is overdue (due date in the past, not completed)
 */
export function isTaskOverdue(task: Task): boolean {
  if (!task.dueAt) return false
  if (task.status === 'completed' || task.status === 'cancelled') return false
  return new Date(task.dueAt).getTime() < Date.now()
}

/**
 * Check if a task is due today
 */
export function isTaskDueToday(task: Task): boolean {
  if (!task.dueAt) return false
  const dueDate = new Date(task.dueAt)
  const today = new Date()
  return (
    dueDate.getDate() === today.getDate() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getFullYear() === today.getFullYear()
  )
}

/**
 * Check if a task is completed or cancelled
 */
export function isTaskCompleted(task: Task): boolean {
  return task.status === 'completed' || task.status === 'cancelled'
}

/**
 * Get priority color class for badges
 */
export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-destructive text-destructive-foreground'
    case 'high':
      return 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/30'
    case 'medium':
      return 'bg-accent text-accent-foreground'
    case 'low':
      return 'bg-secondary text-secondary-foreground'
    default:
      return 'bg-secondary text-secondary-foreground'
  }
}

/**
 * Get priority dot color for compact views
 */
export function getPriorityDotColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'bg-destructive'
    case 'high':
      return 'bg-orange-500'
    case 'medium':
      return 'bg-accent-foreground/50'
    case 'low':
      return 'bg-muted-foreground/30'
    default:
      return 'bg-muted-foreground/30'
  }
}

/**
 * Calculate task statistics from a list of tasks
 */
export function calculateTaskStats(tasks: Task[]) {
  const now = Date.now()
  const today = new Date()

  return {
    total: tasks.length,
    open: tasks.filter((t) => t.status === 'open').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    overdue: tasks.filter((t) => {
      if (!t.dueAt) return false
      if (t.status === 'completed' || t.status === 'cancelled') return false
      return new Date(t.dueAt).getTime() < now
    }).length,
    dueToday: tasks.filter((t) => {
      if (!t.dueAt) return false
      if (t.status === 'completed' || t.status === 'cancelled') return false
      const dueDate = new Date(t.dueAt)
      return (
        dueDate.getDate() === today.getDate() &&
        dueDate.getMonth() === today.getMonth() &&
        dueDate.getFullYear() === today.getFullYear()
      )
    }).length,
  }
}
